import React from 'react';
import { Link } from 'react-router-dom';
import { ScrollText } from 'lucide-react';
import { useSeo } from '../lib/useSeo';

export default function TermsOfService() {
  useSeo({
    title: 'Terms of Service · Larkwell',
    description: 'The terms for using Larkwell — accounts, acceptable use, payments and credits, disclaimers, and how to contact us.',
    canonical: 'https://larkwellapp.com/terms',
  });
  return (
    <div className="min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--foreground))]">
      <div className="max-w-3xl mx-auto px-6 py-14">
        <Link to="/" className="text-sm font-500" style={{ color: 'hsl(var(--primary))' }} data-testid="terms-home-link">← Back to Larkwell</Link>
        <div className="flex items-center gap-2 mt-6 mb-2">
          <ScrollText className="h-6 w-6" style={{ color: 'hsl(var(--primary))' }} />
          <h1 className="font-display text-3xl font-700">Terms of Service</h1>
        </div>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mb-8">Last updated: {new Date().toISOString().slice(0, 10)}</p>

        <div className="space-y-6 text-[15px] leading-relaxed">
          <section>
            <h2 className="font-display text-xl font-600 mb-2">The short version</h2>
            <p>Larkwell is an AI day-planning tool. By using it you agree to these terms. Use it for your own planning, be respectful of the service, and understand that AI output is a helpful aid — not professional advice. If anything's unclear, email us.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-600 mb-2">1. Acceptance &amp; eligibility</h2>
            <p>By creating an account or using Larkwell, you agree to these Terms and to our <Link to="/privacy" className="font-600 underline underline-offset-2" style={{ color: 'hsl(var(--primary))' }}>Privacy &amp; Data policy</Link>. You must be at least 16 years old (or the age of digital consent in your country) to use Larkwell.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-600 mb-2">2. The service</h2>
            <p>Larkwell helps you capture tasks, plan your day, focus, and work with AI assistants. It may connect to third-party services you choose to add (such as Google or Outlook calendars and messaging providers). The service is provided on an “as-is” and “as-available” basis, and we may add, change, or remove features over time.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-600 mb-2">3. AI output is not professional advice</h2>
            <p>Plans, summaries, and suggestions are generated with AI and can be inaccurate or incomplete. They are aids to your own judgement and are not medical, legal, financial, or other professional advice. You are responsible for decisions you make using Larkwell.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-600 mb-2">4. Your account &amp; acceptable use</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Keep your login credentials secure; you're responsible for activity on your account.</li>
              <li>Don't misuse the service — no illegal activity, abuse, attempts to break or overload the system, scraping, or reselling access.</li>
              <li>Don't upload content you don't have the right to use, or that is harmful or unlawful.</li>
            </ul>
            <p className="mt-2">We may suspend or terminate accounts that violate these terms.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-600 mb-2">5. Payments, credits &amp; refunds</h2>
            <p>Some features use credits or paid plans, handled securely by <strong>Stripe</strong> — we never see or store your card details. Prices are shown before purchase and may change going forward.</p>
            <p className="mt-2">{/* EDIT THIS REFUND CLAUSE to match your policy */}Purchased credits and plan fees are generally <strong>non-refundable once purchased</strong>, except where a refund is required by law. If something goes wrong — a failed charge, a technical issue, or credits you were charged for but couldn't use — email us at <a href="mailto:support@larkwellapp.com" className="font-600 underline underline-offset-2" style={{ color: 'hsl(var(--primary))' }}>support@larkwellapp.com</a> within 14 days and we'll make it right on a case-by-case basis. Credits have no cash value and don't expire unless stated at purchase.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-600 mb-2">6. Third-party services</h2>
            <p>When you connect a calendar or messaging provider, your use of that provider is governed by their terms and privacy policies. We're not responsible for third-party services, and connecting them is at your discretion. You can disconnect them at any time in Settings.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-600 mb-2">7. Your content &amp; intellectual property</h2>
            <p>You own the content you create in Larkwell (tasks, notes, projects, etc.). You grant us the limited permission needed to store and process it to run the service for you. Larkwell's own software, branding, and design remain our property. We don't use your private content to train AI models.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-600 mb-2">8. Disclaimers &amp; limitation of liability</h2>
            <p>To the fullest extent permitted by law, Larkwell is provided without warranties of any kind, and we are not liable for indirect, incidental, or consequential damages, or for lost data or profits arising from your use of the service. Our total liability is limited to the amount you paid us in the 12 months before the claim.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-600 mb-2">9. Termination</h2>
            <p>You can stop using Larkwell and delete your account at any time from Settings → Your data &amp; privacy. We may suspend or end access if these terms are violated or if we discontinue the service.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-600 mb-2">10. Changes to these terms</h2>
            <p>We may update these terms as the product evolves. If we make material changes, we'll update the “Last updated” date above and, where appropriate, notify you in the app. Continued use after changes means you accept the updated terms.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-600 mb-2">11. Contact</h2>
            <p>Larkwell is built and operated by an independent solo developer. Questions about these terms? Email us at{' '}
              <a href="mailto:support@larkwellapp.com" className="font-600 underline underline-offset-2" style={{ color: 'hsl(var(--primary))' }} data-testid="terms-contact-email">support@larkwellapp.com</a>.</p>
          </section>
        </div>

        <footer className="mt-12 pt-6 border-t border-[hsl(var(--border))] flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <Link to="/" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors" data-testid="terms-footer-home">Home</Link>
          <Link to="/privacy" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors" data-testid="terms-footer-privacy">Privacy &amp; data</Link>
          <Link to="/help" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors" data-testid="terms-footer-help">How to use Larkwell</Link>
        </footer>
      </div>
    </div>
  );
}
